import { memo, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowsLeftRight, Plus, Spinner } from "@phosphor-icons/react";

const API_BASE_URL = "https://warfc.com/api";

/* ---------------------------------------------------------
   Helpers
--------------------------------------------------------- */
const getFighterImage = (fighter) => {
  if (!fighter) return "/default-fighter.png";

  const img = fighter.image_url || fighter.image;
  if (!img) return "/default-fighter.png";

  return img.startsWith("http")
    ? img
    : `${API_BASE_URL}/${String(img).replace(/^\//, "")}`;
};

const normalizeClass = (val) => {
  if (!val) return null;
  const v = String(val).toLowerCase().trim();

  if (v === "a") return "A";
  if (v === "b") return "B";
  if (v === "c") return "C";
  return null;
};

const autoClass = (fighter) => {
  const fights = Number(fighter?.total_fights || fighter?.fights || 0);

  if (fights >= 20) return "A";
  if (fights >= 10) return "B";
  return "C";
};

const mapFighterToSearchRows = (fighter, rankings = []) => {
  const fighterId = String(fighter?.fighter_id || fighter?.id || "");
  if (!fighterId) return [];

  const entries = rankings.filter(
    (r) => String(r?.fighter_id || "") === fighterId
  );

  if (entries.length === 0) {
    return [
      {
        ...fighter,
        detectedClass: autoClass(fighter),
        displayCategory: "SENIOR",
        rankingCategory: null,
        club: fighter?.club_name || "Independent",
        uniqueKey: `${fighterId}-AUTO`,
      },
    ];
  }

  return entries.map((entry, index) => {
    const isYouth =
      String(entry?.category || "").toLowerCase() === "youth" ||
      String(entry?.age_group || "").toLowerCase() === "youth";

    const cls = isYouth
      ? "N"
      : normalizeClass(entry?.class_division) || autoClass(fighter);

    return {
      ...fighter,
      detectedClass: cls,
      displayCategory: isYouth ? "YOUTH" : "SENIOR",
      rankingCategory: entry?.category || null,
      club: fighter?.club_name || entry?.club_name || "Independent",
      uniqueKey: `${fighterId}-${cls}-${entry?.category || entry?.age_group || "auto"}-${index}`,
    };
  });
};

/* ---------------------------------------------------------
   Internal Search Box
--------------------------------------------------------- */
const SearchBox = memo(function SearchBox({
  token,
  excludedFighterIds = [],
  onSelect,
  placeholder = "Search fighter",
  cornerColor = "red",
  selected = null,
  rankings = [],
  boutClass,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const excludedIdsSet = useMemo(
    () => new Set(excludedFighterIds.filter(Boolean).map((id) => String(id))),
    [excludedFighterIds]
  );

  const displayValue = query || (selected ? `${selected.name} ${selected.surname}` : "");

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    const controller = new AbortController();

    const timeout = setTimeout(async () => {
      setIsSearching(true);

      try {
        const res = await axios.get(
          `${API_BASE_URL}/fighters/search?q=${encodeURIComponent(query.trim())}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            signal: controller.signal,
          }
        );

        const fighters = Array.isArray(res.data) ? res.data : [];
        let rawRows = [];

        fighters.forEach((fighter) => {
          const fighterId = String(fighter?.fighter_id || fighter?.id || "");
          if (!fighterId || excludedIdsSet.has(fighterId)) return;

          rawRows = rawRows.concat(mapFighterToSearchRows(fighter, rankings));
        });

        const uniqueMap = new Map();

        rawRows.forEach((row) => {
          const matchesClass =
            !boutClass ||
            row.detectedClass === boutClass ||
            !row.rankingCategory;

          if (matchesClass && !uniqueMap.has(row.uniqueKey)) {
            uniqueMap.set(row.uniqueKey, row);
          }
        });

        setResults(Array.from(uniqueMap.values()));
      } catch (err) {
        if (!axios.isCancel(err) && err?.name !== "CanceledError") {
          console.error("Fighter search error:", err);
        }
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [query, token, excludedIdsSet, rankings, boutClass]);

  const colorStyles =
    cornerColor === "red"
      ? "border-red-900/40 focus:border-red-600 focus:ring-red-600/20 shadow-[0_0_20px_rgba(220,38,38,0.05)]"
      : "border-blue-900/40 focus:border-blue-600 focus:ring-blue-600/20 shadow-[0_0_20px_rgba(37,99,235,0.05)]";

  return (
    <div className="relative w-full">
      <input
        value={displayValue}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-5 py-4 rounded-xl bg-black border-2 text-white transition-all outline-none font-black uppercase tracking-wider text-sm ${colorStyles}`}
      />

      {isSearching && (
        <Spinner
          className="absolute right-4 top-4 animate-spin text-cyan-500"
          size={20}
        />
      )}

      {results.length > 0 && (
        <ul className="absolute z-[110] w-full bg-[#0d0d0d] border border-white/10 rounded-xl mt-2 overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.8)] max-h-80 overflow-y-auto backdrop-blur-2xl">
          {results.map((fighter) => (
            <li
              key={fighter.uniqueKey}
              onClick={() => {
                onSelect?.({
                  ...fighter,
                  resolved_class: fighter.detectedClass,
                });
                setQuery("");
                setResults([]);
              }}
              className="p-4 hover:bg-white/10 cursor-pointer flex justify-between items-center border-b border-white/5 last:border-0 transition-colors group"
            >
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={getFighterImage(fighter)}
                  className="w-10 h-10 rounded-full border-2 border-white/10 object-cover shrink-0"
                  alt=""
                />

                <div className="min-w-0">
                  <p className="font-[1000] text-white uppercase italic text-xs tracking-tighter truncate">
                    {fighter.name} {fighter.surname}
                  </p>

                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span
                      className={`text-[8px] px-1.5 py-0.5 font-[1000] rounded border transition-colors
                        ${
                          fighter.detectedClass === "A"
                            ? "bg-yellow-500/20 text-yellow-500 border-yellow-500/30"
                            : fighter.detectedClass === "B"
                              ? "bg-gray-400/20 text-gray-300 border-gray-400/30"
                              : fighter.detectedClass === "C"
                                ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                                : "bg-purple-500/20 text-purple-300 border-purple-500/30"
                        }`}
                    >
                      {fighter.detectedClass} CLASS
                    </span>

                    <span className="text-[7px] font-black text-white/40 border border-white/10 px-1 rounded">
                      {fighter.displayCategory}
                    </span>

                    <p className="text-[9px] text-gray-500 uppercase font-black tracking-widest truncate max-w-[140px]">
                      {fighter.club}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                <span className="text-[10px] font-black uppercase italic">
                  Select
                </span>
                <Plus weight="bold" size={16} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

/* ---------------------------------------------------------
   Main Matchmaker
--------------------------------------------------------- */
export default function EventMatchmaker({
  token,
  rankings = [],
  boutClass,
  setBoutClass,
  f1,
  setF1,
  f2,
  setF2,
  rounds,
  setRounds,
  weight,
  setWeight,
  creating,
  onCreateBout,
}) {
  return (
    <section className="mb-32 p-6 md:p-12 rounded-[2.5rem] bg-neutral-900/50 backdrop-blur-xl border border-white/10 shadow-3xl">
      <div className="flex justify-between items-center mb-10">
        <h3 className="text-3xl font-black uppercase italic tracking-tighter">
          Matchmaker
        </h3>

        <div className="flex items-center gap-3">
          <span className="text-[10px] font-black text-white/40 uppercase tracking-widest">
            Select Division
          </span>
          <select
            value={boutClass}
            onChange={(e) => setBoutClass(e.target.value)}
            className="bg-black border-2 border-cyan-500/30 text-cyan-500 font-black px-4 py-2 rounded-xl uppercase italic text-sm outline-none focus:border-cyan-500 transition-all"
          >
            <option value="N">N Class</option>
            <option value="C">C Class</option>
            <option value="B">B Class</option>
            <option value="A">A Class</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6 items-start mb-10">
        <SearchBox
          token={token}
          onSelect={setF1}
          excludedFighterIds={[f2?.fighter_id]}
          placeholder="Red Corner"
          cornerColor="red"
          selected={f1}
          rankings={rankings}
          boutClass={boutClass}
        />

        <button
          type="button"
          onClick={() => {
            const temp = f1;
            setF1(f2);
            setF2(temp);
          }}
          className="p-4 rounded-full bg-white/5 border border-white/10 self-center mt-1 hover:bg-white/10 transition-all"
        >
          <ArrowsLeftRight size={24} className="text-cyan-500" />
        </button>

        <SearchBox
          token={token}
          onSelect={setF2}
          excludedFighterIds={[f1?.fighter_id]}
          placeholder="Blue Corner"
          cornerColor="blue"
          selected={f2}
          rankings={rankings}
          boutClass={boutClass}
        />
      </div>

      <div className="flex flex-col md:flex-row gap-4 max-w-4xl mx-auto">
        <input
          value={rounds}
          onChange={(e) => setRounds(e.target.value)}
          placeholder="ROUNDS (3x2)"
          className="flex-1 bg-black border border-white/10 p-4 rounded-xl font-black text-center focus:border-cyan-500 outline-none uppercase italic text-sm"
        />

        <input
          value={weight}
          onChange={(e) => setWeight(e.target.value)}
          placeholder="WEIGHT (77KG)"
          className="flex-1 bg-black border border-white/10 p-4 rounded-xl font-black text-center focus:border-cyan-500 outline-none uppercase italic text-sm"
        />

        <button
          type="button"
          onClick={onCreateBout}
          disabled={creating || !f1 || !f2 || !weight}
          className="bg-white text-black px-10 py-4 rounded-xl font-black uppercase italic transition-all hover:bg-cyan-500 disabled:bg-neutral-800 disabled:text-neutral-500"
        >
          {creating ? "Saving..." : "Create Bout"}
        </button>
      </div>
    </section>
  );
}